import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useChat } from "../store/chat";
import { AppShell } from "../components/AppShell";
import { Sidebar } from "../components/Sidebar";
import { ChatPanel } from "../components/ChatPanel";

export function ChatPage() {
  const params = useParams();
  const navigate = useNavigate();
  const sessions = useChat((s) => s.sessions);
  const activeSessionId = useChat((s) => s.activeSessionId);
  const selectSession = useChat((s) => s.selectSession);
  const createSession = useChat((s) => s.createSession);

  // Session list is owned by Sidebar (loaded on mount + on route change), so
  // ChatPage doesn't fetch it. The sync-URL effect below still depends on
  // `sessions` for redirect-to-most-recent — by the time it runs, Sidebar's
  // load has populated the store.

  // URL -> session. Deliberately re-selects even when the id matches the
  // store's activeSessionId: the store survives navigation to /report and
  // back, but the persisted history may have moved on (e.g. the user's last
  // message landed while we were away), so every (re)mount refreshes it.
  // selectSession keeps current messages on screen for a same-id refresh, so
  // there's no flicker. Run state is NOT touched here — ChatPanel's
  // useSessionRun observes the session's run independently.
  useEffect(() => {
    const idStr = params.sessionId;
    if (!idStr) return;
    const id = Number(idStr);
    if (!Number.isFinite(id) || id <= 0) {
      navigate("/chat", { replace: true });
      return;
    }
    void selectSession(id).catch((err) => {
      console.error("failed to load session", err);
      navigate("/chat", { replace: true });
    });
  }, [params.sessionId, selectSession, navigate]);

  // No id in URL: redirect to most recent chat if any. The chat list is
  // already filtered to non-promoted chats server-side, so sessions[0]
  // is always a safe pick.
  useEffect(() => {
    if (params.sessionId) return;
    if (!activeSessionId && sessions.length > 0) {
      navigate(`/chat/${sessions[0].id}`, { replace: true });
    }
  }, [params.sessionId, sessions, activeSessionId, navigate]);

  const onCreate = useCallback(async () => {
    const session = await createSession();
    navigate(`/chat/${session.id}`);
  }, [createSession, navigate]);

  return (
    <AppShell sidebar={<Sidebar onCreate={() => void onCreate()} />}>
      <ChatPanel onCreateSession={onCreate} />
    </AppShell>
  );
}
